import {
  ChannelType,
  PurchaseLikelihoodList,
} from "@intelligems/ig-types/schemas/analytics/base";
import {
  ExperienceFe,
  GenericExperienceFe,
} from "@intelligems/ig-types/fe-only/experiences";
import { ExperienceKeyMetricFe } from "@intelligems/ig-types/fe-only/custom-events";
import { ExperienceListVariationDto } from "@intelligems/ig-types/dtos/variations";
import {
  GenericVariationFe,
  VariationFe,
} from "@intelligems/ig-types/fe-only/variations";
import { GraphConfig } from "@intelligems/ig-types/schemas/analytics/custom-graphs";
import {
  STANDARD_EVENTS_ALLOWED_KEY_METRICS,
  STANDARD_EVENTS_DISALLOWED_KEY_METRICS,
} from "@intelligems/ig-types/dtos/analytics-events/standard-events";
import { DashboardPageName } from "@src/api/types/dashboards";
import { overviewTableSortOrder } from "@src/utils/sortings";

export const getTabName = (metricId: string): DashboardPageName => {
  if (
    metricId.includes("conversion") ||
    metricId.includes("cart") ||
    metricId.includes("checkout") ||
    metricId.includes("view")
  ) {
    return "conversion";
  }

  if (
    metricId.includes("revenue") &&
    !metricId.includes("shipping") &&
    !metricId.includes("subscription")
  ) {
    return "revenue";
  }

  if (metricId.includes("profit") && !metricId.includes("subscription")) {
    return "profit";
  }

  if (metricId.includes("shipping")) {
    return "shipping";
  }

  if (metricId.includes("subscription")) {
    return "subscriptions";
  }

  if (metricId.includes("order") || metricId.includes("unit")) {
    return "orders";
  }

  return "customMetrics";
};

export const getMetricCategoryName = (metricId: string): string => {
  if (
    metricId.includes("conversion") ||
    metricId.includes("cart") ||
    metricId.includes("checkout") ||
    metricId.includes("view")
  ) {
    return "Conversion";
  }

  if (
    metricId.includes("revenue") &&
    !metricId.includes("shipping") &&
    !metricId.includes("subscription")
  ) {
    return "Revenue";
  }

  if (metricId.includes("profit") && !metricId.includes("subscription")) {
    return "Profit";
  }

  if (metricId.includes("shipping")) {
    return "Shipping";
  }

  if (metricId.includes("subscription")) {
    return "Subscription";
  }

  if (metricId == "avg_units_per_order") {
    return "Revenue";
  }
  if (metricId.includes("discount")) {
    return "Discount";
  }
  if (metricId.includes("order") || metricId.includes("unit")) {
    return "Order";
  }

  return "Custom";
};

export const getIsPrimaryMetric = (
  config: GraphConfig,
  pageName: DashboardPageName,
  experienceKeyMetrics: ExperienceKeyMetricFe[]
) => {
  if (pageName !== "overview") {
    return false;
  }
  if (experienceKeyMetrics[0].standardEventId) {
    return (
      config.metrics[0] ===
      STANDARD_EVENTS_ALLOWED_KEY_METRICS[
        experienceKeyMetrics[0].standardEventId
      ].analyticsMetricId
    );
  } else if (experienceKeyMetrics[0].experienceCustomMetric) {
    return (
      config.metrics[0] ===
      experienceKeyMetrics[0].experienceCustomMetric?.customEventId
    );
  }
  return false;
};

export const sortAndFlattenData = (
  groups: Record<string, any[]>,
  audienceOrder: string[]
) => {
  return audienceOrder.length > 0
    ? Object.keys(groups)
        .sort((a, b) => {
          const indexA = audienceOrder.indexOf(a);
          const indexB = audienceOrder.indexOf(b);
          return indexA - indexB;
        })
        .flatMap((key) => groups[key])
        .filter((data) => data.audience !== "")
    : Object.keys(groups)
        .sort((keyA, keyB) => {
          const orderA =
            overviewTableSortOrder[keyA] ?? overviewTableSortOrder.Other;
          const orderB =
            overviewTableSortOrder[keyB] ?? overviewTableSortOrder.Other;
          return orderA - orderB;
        })
        .flatMap((key) => groups[key]);
};

export const normalizeValues = (obj: any) => {
  return Object.keys(obj).reduce((acc, key) => {
    acc[key] = obj[key];
    return acc;
  }, {} as any);
};

export const sortEventsManager = (filteredRowData: any[]) => {
  return filteredRowData
    .map((item, index) => ({ ...item, originalIndex: index })) // Add original index
    .sort((a, b) => {
      // Check if items are of type CombinedCustomEvent (with id property)
      const aHasId = "id" in a;
      const bHasId = "id" in b;

      // Sort events with id after those without id
      if (aHasId !== bHasId) {
        return aHasId ? 1 : -1; // Events without id come first
      }

      // Check archivedAtTs if both items are CombinedCustomEvents
      const isArchivedA = "archivedAtTs" in a && a.archivedAtTs ? 1 : 0;
      const isArchivedB = "archivedAtTs" in b && b.archivedAtTs ? 1 : 0;

      if (isArchivedA !== isArchivedB) {
        return isArchivedA - isArchivedB; // Archived events come last
      }

      // Check if tbEvent exists
      const aHasTbEvent = "tbEvent" in a;
      const bHasTbEvent = "tbEvent" in b;

      // Sort events without tbEvent last but before archived
      if (aHasTbEvent !== bHasTbEvent) {
        return aHasTbEvent ? -1 : 1; // Events without tbEvent come last but before archived
      }

      // Convert last_event_ts to Unix timestamp for comparison
      const aLastEventTs = a.tbEvent?.last_event_ts
        ? new Date(a.tbEvent.last_event_ts).getTime()
        : a.last_event_ts
          ? new Date(a.last_event_ts).getTime()
          : -Infinity;
      const bLastEventTs = b.tbEvent?.last_event_ts
        ? new Date(b.tbEvent.last_event_ts).getTime()
        : b.last_event_ts
          ? new Date(b.last_event_ts).getTime()
          : -Infinity;

      if (aLastEventTs !== bLastEventTs) {
        return bLastEventTs - aLastEventTs; // Descending order
      }

      // Preserve original order for other events
      return a.originalIndex - b.originalIndex;
    })
    .map(({ originalIndex, ...item }: any) => item); // Remove original index after sorting
};

export const getStandardMetricByAnalyticsMetricId = (
  analyticsMetricId: string
) => {
  return (
    Object.values(STANDARD_EVENTS_ALLOWED_KEY_METRICS).find(
      (metric) => metric.analyticsMetricId === analyticsMetricId
    ) ||
    Object.values(STANDARD_EVENTS_DISALLOWED_KEY_METRICS).find(
      (metric) => metric.analyticsMetricId === analyticsMetricId
    )
  );
};

export const metricsConfig: Record<
  string,
  { numerator: string; denominator: string }
> = {
  add_to_cart_rate: {
    numerator: "n_visitors_added_to_cart",
    denominator: "n_visitors",
  },
  checkout_begin_rate: {
    numerator: "n_visitors_checkout_started",
    denominator: "n_visitors",
  },
  abandoned_cart_rate: {
    numerator: "n_visitors_abandoned_cart",
    denominator: "n_visitors_added_to_cart",
  },
  abandoned_checkout_rate: {
    numerator: "n_visitors_abandoned_checkout",
    denominator: "n_visitors_checkout_started",
  },
  checkout_enter_contact_info_rate: {
    numerator: "n_visitors_checkout_contact_info_submitted",
    denominator: "n_visitors",
  },
  checkout_address_submitted_rate: {
    numerator: "n_visitors_checkout_address_info_submitted",
    denominator: "n_visitors",
  },
  view_product_page_rate: {
    numerator: "n_visitors_viewed_product_page",
    denominator: "n_visitors",
  },
  view_collection_page_rate: {
    numerator: "n_visitors_viewed_collection_page",
    denominator: "n_visitors",
  },
  conversion_rate: {
    numerator: "n_orders",
    denominator: "n_visitors",
  },
  net_revenue_per_visitor: {
    numerator: "net_revenue",
    denominator: "n_visitors",
  },
  net_revenue_per_order: {
    numerator: "net_revenue",
    denominator: "n_orders",
  },
  net_product_revenue_per_order: {
    numerator: "net_product_revenue",
    denominator: "n_orders",
  },
  avg_units_per_order: {
    numerator: "total_units",
    denominator: "n_orders",
  },
  avg_product_revenue_per_unit: {
    numerator: "net_product_revenue",
    denominator: "total_units",
  },
  pct_discounted_orders: {
    numerator: "n_orders_discounted",
    denominator: "n_orders",
  },
  avg_discount_per_discounted_order: {
    numerator: "total_discount",
    denominator: "n_orders_discounted",
  },
  avg_discount_per_all_orders: {
    numerator: "total_discount",
    denominator: "n_orders",
  },
  discount_pct_discounted_order_gross_revenue: {
    numerator: "total_discount",
    denominator: "discounted_order_gross_revenue",
  },
  discount_pct_total_gross_revenue: {
    numerator: "total_discount",
    denominator: "gross_revenue",
  },
  discount_pct_discounted_order_gross_profit: {
    numerator: "total_discount",
    denominator: "discounted_order_gross_profit",
  },
  discount_pct_total_gross_profit: {
    numerator: "total_discount",
    denominator: "gross_profit",
  },
  gross_margin_pct: {
    numerator: "gross_profit",
    denominator: "net_revenue",
  },
  gross_profit_per_visitor: {
    numerator: "gross_profit",
    denominator: "n_visitors",
  },
  gross_profit_per_order: {
    numerator: "gross_profit",
    denominator: "n_orders",
  },
  net_shipping_revenue_per_order: {
    numerator: "net_shipping_revenue",
    denominator: "n_orders",
  },
  pct_orders_free_shipping: {
    numerator: "n_orders_free_shipping",
    denominator: "n_orders",
  },
  subscription_orders_per_visitor: {
    numerator: "n_subscription_orders",
    denominator: "n_visitors",
  },
  subscription_revenue_per_visitor: {
    numerator: "subscription_net_revenue",
    denominator: "n_visitors",
  },
  subscription_gross_profit_per_visitor: {
    numerator: "subscription_gross_profit",
    denominator: "n_visitors",
  },
  pct_subscription_orders: {
    numerator: "n_subscription_orders",
    denominator: "n_orders",
  },
  subscription_revenue_per_order: {
    numerator: "subscription_net_revenue",
    denominator: "n_subscription_orders",
  },
  subscription_product_revenue_per_order: {
    numerator: "subscription_product_revenue",
    denominator: "n_orders",
  },
  subscription_shipping_revenue_per_order: {
    numerator: "subscription_shipping_revenue",
    denominator: "n_orders",
  },
  subscription_units_per_subscription_order: {
    numerator: "subscription_shipping_revenue",
    denominator: "subscription_units",
  },
  subscription_product_revenue_per_unit: {
    numerator: "subscription_product_revenue",
    denominator: "subscription_units",
  },
  pct_orders_with_checkout_upsell: {
    numerator: "n_orders_with_checkout_upsell_product",
    denominator: "n_orders",
  },
  net_revenue_from_checkout_upsell: {
    numerator: "net_revenue_from_checkout_upsell",
    denominator: "net_revenue",
  },
  pct_orders_with_onsite_upsell: {
    numerator: "n_orders_with_onsite_upsell_product",
    denominator: "n_orders",
  },
  net_revenue_from_onsite_upsell: {
    numerator: "net_revenue_from_onsite_upsell",
    denominator: "net_revenue",
  },
  post_orders_per_customer: {
    numerator: "n_post_orders",
    denominator: "n_customers",
  },
  post_orders_per_visitor: {
    numerator: "n_post_orders",
    denominator: "n_visitors",
  },
  post_revenue_per_customer: {
    numerator: "post_net_revenue",
    denominator: "n_customers",
  },
  post_revenue_per_visitor: {
    numerator: "post_net_revenue",
    denominator: "n_visitors",
  },
  post_profit_per_customer: {
    numerator: "post_gross_profit",
    denominator: "n_customers",
  },
  post_profit_per_visitor: {
    numerator: "post_gross_profit",
    denominator: "n_visitors",
  },
};

const validChannelAndTraffic: ChannelType[] = [
  "Google",
  "Facebook",
  "Youtube",
  "Organic Social",
  "Paid Social",
  "Paid Search",
  "Paid Shopping",
  "Email",
  "Instagram",
  "TikTok",
  "Twitter",
  "Referral",
  "Organic Search",
  "Direct",
  "SMS",
];

export const isRolloutGroupDisabled = ({
  hasPersonalizationAccess,
  channelOrTrafficSrc,
  purchaseLikelihood,
  foundVariation,
  variationHasCheckoutBlocks,
  experience,
  tabValue,
}: {
  hasPersonalizationAccess: boolean;
  foundVariation?: VariationFe;
  variationHasCheckoutBlocks?: boolean;
  channelOrTrafficSrc?: string;
  purchaseLikelihood?: string;
  experience: GenericExperienceFe | ExperienceFe;
  tabValue: number;
}) => {
  if (experience.testTypes.hasTestShipping) {
    return {
      isDisabled: true,
      hideButton: true,
      reason: "Roll outs are not permitted for shipping tests.",
    };
  }

  if (!hasPersonalizationAccess) {
    return {
      isDisabled: true,
      hideButton: true,
      reason:
        "You do not have access to this feature. Contact sales@intelligems.io for more information.",
    };
  }

  if (experience.testTypes.hasTestPricing && experience.status === "ended") {
    return {
      isDisabled: true,
      hideButton: true,
      reason: "Ended price tests cannot be rolled out.",
    };
  }

  if (experience.testTypes.hasTestPricing && tabValue !== 0) {
    return {
      isDisabled: true,
      hideButton: true,
      reason: "Price tests cannot be rolled out to a specific audience.",
    };
  }

  const nonCaseSensitiveValidSources = validChannelAndTraffic.map((c) =>
    c.toLowerCase()
  );
  if (
    channelOrTrafficSrc &&
    !nonCaseSensitiveValidSources.includes(channelOrTrafficSrc.toLowerCase())
  ) {
    return {
      isDisabled: true,
      hideButton: false,
      reason: `${channelOrTrafficSrc} cannot be rolled out.`,
    };
  }

  if (
    purchaseLikelihood &&
    !(PurchaseLikelihoodList as readonly string[]).includes(purchaseLikelihood)
  ) {
    return {
      isDisabled: true,
      hideButton: false,
      reason: `${purchaseLikelihood} purchase likelihood cannot be rolled out.`,
    };
  }

  if (experience.testTypes.hasTestCampaign && !foundVariation?.offer) {
    return {
      isDisabled: true,
      hideButton: false,
      reason: "Groups with no offer associated with them cannot be rolled out.",
    };
  }

  if (
    experience.testTypes.hasTestCheckoutBlocks &&
    !variationHasCheckoutBlocks
  ) {
    return {
      isDisabled: true,
      hideButton: false,
      reason:
        "Groups with no checkout modification associated with them cannot be rolled out.",
    };
  }
  return {
    isDisabled: false,
    hideButton: false,
    reason: "",
  };
};
